/**
 * @fileoverview AI Settings Types for FlowState Platform
 * @description Types for user AI preferences, model configuration, and BYOK (Bring Your Own Key)
 * @module @flowstate/types/ai-settings
 * @version 1.0.0
 * @license MIT
 * @white-label Ready for TradieGate and future white-label deployments
 */

/**
 * Supported AI model providers.
 *
 * - `'gemini'`    — Google Gemini API (canonical key)
 * - `'openai'`    — OpenAI GPT models
 * - `'anthropic'` — Anthropic Claude models
 * - `'flowstate'` — legacy alias for `'gemini'`, kept only so stored settings keep
 *   loading. FlowState does not fund user inference; see `docs/protocols/mcp/MCP_SERVER_GUIDE.md`.
 * - `'default'`   — Use the system / org-level default provider
 */
export type AIProvider = 
  | 'gemini'      // Google Gemini models (canonical)
  | 'openai'      // OpenAI GPT models
  | 'anthropic'   // Anthropic Claude models
  | 'openrouter'  // OpenRouter — federated access to 200+ models
  | 'flowstate'   // Legacy alias for 'gemini'
  | 'default';    // System/org default

/**
 * Gemini models available for user selection (direct Gemini API).
 * IDs per https://ai.google.dev/gemini-api/docs/models (verified 2026-06-28).
 */
export const GEMINI_MODELS = [
  { id: 'gemini-3.1-flash-lite', name: 'Gemini 3.1 Flash-Lite', description: 'Fastest stable — extraction, routing, classification', tier: 'free', contextWindow: 1_048_576 },
  { id: 'gemini-3.5-flash', name: 'Gemini 3.5 Flash', description: 'Frontier intelligence — agentic workflows and coding (stable)', tier: 'free', contextWindow: 1_048_576 },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: 'Deep reasoning and complex tasks (stable)', tier: 'free', contextWindow: 2_097_152 },
  { id: 'gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro (Preview)', description: 'Most capable — preview, paid tier', tier: 'paid', contextWindow: 2_097_152 },
] as const;

export type GeminiModelId = typeof GEMINI_MODELS[number]['id'];

/** Canonical default Gemini model IDs — single source of truth for the whole monorepo. */
export const DEFAULT_GEMINI_FLASH_MICRO_MODEL = 'gemini-3.1-flash-lite' as const;
export const DEFAULT_GEMINI_FLASH_MODEL = 'gemini-3.5-flash' as const;
export const DEFAULT_GEMINI_PRO_MODEL = 'gemini-2.5-pro' as const;

/**
 * Retired or internal-only IDs → current stable Gemini API names.
 * `gemini-3.5-flash-micro` was never a Google model code (use 3.1-flash-lite).
 * Gemini 2.0 family shut down 2026-06-01 per Google deprecations page.
 */
export const GEMINI_MODEL_ALIASES: Record<string, string> = {
  'gemini-3.5-flash-micro': DEFAULT_GEMINI_FLASH_MICRO_MODEL,
  'gemini-2.5-flash': DEFAULT_GEMINI_FLASH_MODEL,
  'gemini-2.5-flash-lite': DEFAULT_GEMINI_FLASH_MICRO_MODEL,
  'gemini-2.0-flash': DEFAULT_GEMINI_FLASH_MODEL,
  'gemini-2.0-flash-lite': DEFAULT_GEMINI_FLASH_MICRO_MODEL,
  'gemini-2.0-flash-exp': DEFAULT_GEMINI_FLASH_MODEL,
  'gemini-1.5-flash': DEFAULT_GEMINI_FLASH_MODEL,
  'gemini-1.5-flash-8b': DEFAULT_GEMINI_FLASH_MICRO_MODEL,
  'gemini-1.5-pro': DEFAULT_GEMINI_PRO_MODEL,
};

/** Normalize `google/` / `models/` prefixes and map retired IDs to live API model names. */
export function resolveGeminiModelId(model: string): string {
  const bare = model.replace(/^models\//, '').replace(/^google\//, '').trim();
  return GEMINI_MODEL_ALIASES[bare] ?? bare;
}

/** Surface-specific defaults derived from the canonical IDs so they update together. */
export const DEFAULT_LANDING_CHAT_MODEL = DEFAULT_GEMINI_FLASH_MICRO_MODEL;
export const DEFAULT_BRAIN_CHAT_MODEL = DEFAULT_GEMINI_FLASH_MODEL;
export const DEFAULT_SUMMARIZER_MODEL = DEFAULT_GEMINI_FLASH_MICRO_MODEL;
export const DEFAULT_FUNCTION_MODEL = DEFAULT_GEMINI_FLASH_MODEL;

/**
 * Available models — Google Gemini-centric
 */
export type AIModel = 
  // Google Gemini
  | 'google/gemini-3.1-flash-lite'
  | 'google/gemini-3.5-flash'
  | 'google/gemini-2.5-pro'
  | 'google/gemini-3.1-pro-preview'
  // Custom string for user-selected models
  | string;

/**
 * Durable credential state surfaced to UI gating logic.
 *
 * This is derived, not persisted directly; it answers "can this user chat now?"
 * across Gemini OAuth, Gemini BYOK, OpenRouter BYOK, and platform-funded OpenRouter.
 */
export type AICredentialStatus =
  | 'none'
  | 'gemini_oauth'
  | 'gemini_byok'
  | 'openrouter_byok'
  | 'openrouter_platform';

/**
 * Agent Box CLI provisioning preferences.
 *
 * Controls which CLIs are installed when a principal's Upstash Box is
 * provisioned or resumed. Gemini CLI is always enabled.
 *
 * Sensitive field: `githubToken` is stored in localStorage only and MUST NOT
 * be written to Supabase `mycelium_principals.preferences`.
 */
export interface BoxAgentPreferences {
  /**
   * CLI toggles. Keys match registered CLI ids in the agent CLI registry
   * (e.g. 'opencode', 'copilot', 'openclaw', 'hermes'). Default availability
   * is determined by each CLI's category ('core' = on by default, 'opt-in' = off).
   */
  installCLIs?: Record<string, boolean>;
  /**
   * GitHub personal access token used to authenticate `@github/copilot-cli`.
   * SENSITIVE — stored in localStorage only, never synced to Supabase.
   */
  githubToken?: string;
}

/**
 * User's AI settings stored in their profile.
 * The `version` field guards against silent breakage when new required fields
 * are added. Increment `CURRENT_AI_SETTINGS_VERSION` whenever a required field
 * is introduced and provide a migration in the settings-migration utility.
 */
export const CURRENT_AI_SETTINGS_VERSION = 2;

export interface UserAISettings {
  /**
   * Schema version — increment when required fields change.
   * Absence of this field implies version 1 (legacy).
   */
  version?: number;

  /** Active AI provider for Cortex (uses the canonical AIProvider union) */
  provider?: AIProvider;

  /** Whether user wants to use their own API key */
  useOwnKey: boolean;

  /** User-supplied Gemini API key (local cache; also persisted server-side via RPC for quota fallback) */
  geminiApiKey?: string;

  /** True when a BYOK key is stored server-side but not loaded into this device cache */
  geminiByokOnServer?: boolean;

  /** User-supplied OpenRouter API key (local cache; persisted server-side via RPC) */
  openrouterApiKey?: string;

  /** True when an OpenRouter BYOK key is stored server-side but not loaded into this device cache */
  openrouterApiKeyOnServer?: boolean;

  /** Whether the user has connected their Google account for Gemini API access (OAuth).
   *  Defaults to true when connected — set to false to temporarily disable without disconnecting. */
  useGoogleGemini?: boolean;

  /** Derived credential readiness state. UI gates use this to decide whether Cortex is usable. */
  aiCredentialStatus?: AICredentialStatus;

  /** Primary model used for main chat responses (Gemini model name, e.g. DEFAULT_GEMINI_FLASH_MODEL) */
  primaryModel?: string;

  /** Reasoning effort for the primary chat model (models without reasoning support ignore it) */
  reasoningEffort?: ReasoningEffort;

  /** Sub-agent model used for specialist/tool calls */
  subAgentModel?: string;

  /** Function model used for brain-dump, task-enhancer and other utility edge functions */
  functionModel?: string;

  /** User's preferred model (global default) */
  preferredModel: AIModel;

  /** Task-specific model overrides */
  overrides?: {
    coding?: AIModel;
    reasoning?: AIModel;
    general?: AIModel;
    vision?: AIModel;
    fast?: AIModel;
  };
  
  /** Model generation settings */
  modelConfig?: AIModelConfig;
  
  /**
   * Agent Box provisioning preferences (CLI toggles + GitHub token).
   * `githubToken` within this object is localStorage-only — stripped before
   * syncing to `mycelium_principals.preferences.ai`.
   */
  box?: BoxAgentPreferences;

  /** Created timestamp */
  createdAt?: string;
  
  /** Last updated timestamp */
  updatedAt?: string;
}

/**
 * Model configuration options
 */
export interface AIModelConfig {
  /** Temperature (0-2, default 0.7) */
  temperature?: number;
  
  /** Maximum tokens to generate (default 2048) */
  maxTokens?: number;
  
  /** Top P nucleus sampling (default 0.95) */
  topP?: number;
  
  /** Top K sampling (default 40) */
  topK?: number;
  
  /** Frequency penalty (-2 to 2) */
  frequencyPenalty?: number;
  
  /** Presence penalty (-2 to 2) */
  presencePenalty?: number;
}

/**
 * Chat request payload
 */
export interface ChatRequest {
  /** Conversation messages */
  messages: ChatMessage[];
  
  /** Agent context for C.A.L.I */
  agentContext?: AgentContextPayload;
  
  /** Enable streaming (default true) */
  stream?: boolean;
  
  /** User's AI settings for BYOK */
  userAISettings?: UserAISettings;
}

/**
 * Chat message format
 */
export interface ChatMessage {
  /** Message role */
  role: 'user' | 'assistant' | 'system';
  
  /** Message content */
  content: string;
  
  /** Optional attachments */
  attachments?: MessageAttachment[];
}

/**
 * Message attachment
 */
export interface MessageAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
}

/**
 * Props for ChatInput component
 */
export interface ChatInputProps {
  input: string;
  setInput: (input: string | ((prev: string) => string)) => void;
  handleSendMessage: () => void;
  isLoading: boolean;
  conversationId?: string;
  onFileUploaded?: (attachment: MessageAttachment) => void;
  pendingAttachments?: MessageAttachment[];
  onWebSearchClick?: () => void;
  onWorkflowClick?: () => void;
  selectedSpecialist?: string;
  onSpecialistChange?: (specialist: string | undefined) => void;
  /** Active model provider (Gemini direct vs OpenRouter federated). */
  selectedProvider?: 'gemini' | 'openrouter';
  onProviderChange?: (provider: 'gemini' | 'openrouter') => void;
  /** Active model id within the chosen provider. */
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  /** True when an active Google OAuth session can delegate Gemini calls. */
  isGeminiDelegated?: boolean;
}

/**
 * User-facing specialists available for direct selection
 */
export const USER_ACCESSIBLE_SPECIALISTS = [
  { id: 'legal', name: 'Legal', description: 'Trust law, case research, civil matters', icon: '⚖️' },
  { id: 'research', name: 'Research', description: 'Web search, fact-checking, analysis', icon: '🔍' },
  { id: 'task-management', name: 'Tasks', description: 'Task management and scheduling', icon: '📋' },
  { id: 'sales', name: 'Sales', description: 'Pipeline, deals, commissions', icon: '💰' },
  { id: 'crm', name: 'CRM', description: 'Client management and analytics', icon: '👥' },
  { id: 'accounting', name: 'Accounting', description: 'Transactions, subscriptions, Xero', icon: '📊' },
  { id: 'reporting', name: 'Reporting', description: 'Reports and analytics', icon: '📈' },
] as const;

export type UserAccessibleSpecialist = typeof USER_ACCESSIBLE_SPECIALISTS[number]['id'];

/**
 * Agent context passed to Cortex
 */
export interface AgentContextPayload {
  /** User's role */
  role?: string;
  
  /** Current scope/page */
  scope?: string;
  
  /** User's capabilities */
  capabilities?: Record<string, boolean>;
  
  /** Domain expertise */
  expertise?: {
    domain: string;
    description: string;
  };
  
  /** Aggregated summary of selected past conversations */
  summarizedContext?: string;

  /** Additional context */
  [key: string]: unknown;
}

/**
 * Model info for UI display
 */
export interface AIModelInfo {
  id: AIModel;
  name: string;
  provider: AIProvider;
  description: string;
  contextWindow: number;
  inputCostPer1M?: number;
  outputCostPer1M?: number;
  supportsVision?: boolean;
  supportsFunctionCalling?: boolean;
}

/**
 * Reasoning effort for models that support adjustable thinking/reasoning depth.
 * Passed through to OpenRouter as `reasoning.effort` and to Gemini as
 * `generationConfig.thinkingConfig.thinkingBudget` (mapped server-side).
 */
export type ReasoningEffort = 'low' | 'medium' | 'high';

/**
 * Enriched model row served by the brain `GET /model-catalog` endpoint and
 * consumed by the shared Cortex ModelPicker. Live provider metadata (cost,
 * context, capabilities) is merged over curated preset metadata; curated
 * fields act as the fallback when a model is missing from the live catalog.
 */
export interface CatalogModel {
  id: string;
  provider: 'gemini' | 'openrouter';
  /** Display name (curated label preferred over raw slug). */
  name: string;
  /** Provider family — vendor grouping for OpenRouter ('Anthropic', 'DeepSeek', …), 'Google' for Gemini. */
  vendor: string;
  /** Outcome copy, e.g. 'Fastest stable — extraction, routing, classification'. */
  description?: string;
  /** Curated tier ('free' | 'paid' | 'auto' | 'flash' | 'pro'). */
  tier?: string;
  contextWindow?: number;
  /** USD per 1M tokens. `0` input+output means free. */
  costPer1M?: { input: number; output: number };
  capabilities: {
    reasoning: boolean;
    tools: boolean;
    vision: boolean;
    structuredOutput: boolean;
  };
  /** Row badge: Free / Auto / New / Beta / Paid. */
  status?: 'free' | 'auto' | 'new' | 'beta' | 'paid';
  /** Search aliases derived from id/name ('claude', 'sonnet', 'gpt-5', …). */
  keywords: string[];
}

/** Response body of `GET /model-catalog` on the brain edge function. */
export interface ModelCatalogResponse {
  models: CatalogModel[];
}

/**
 * Available models for selection
 */
export const AI_MODELS: AIModelInfo[] = [
  // Google Gemini — Free Tier
  {
    id: 'google/gemini-3.1-flash-lite',
    name: 'Gemini 3.1 Flash-Lite',
    provider: 'gemini',
    description: 'Fastest stable — extraction, routing, classification',
    contextWindow: 1000000,
    inputCostPer1M: 0.10,
    outputCostPer1M: 0.40,
    supportsVision: true,
    supportsFunctionCalling: true,
  },
  {
    id: 'google/gemini-3.5-flash',
    name: 'Gemini 3.5 Flash',
    provider: 'gemini',
    description: 'Frontier intelligence — agentic workflows and coding',
    contextWindow: 1000000,
    inputCostPer1M: 0.30,
    outputCostPer1M: 2.50,
    supportsVision: true,
    supportsFunctionCalling: true,
  },
  {
    id: 'google/gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    provider: 'gemini',
    description: '2M context, best reasoning (free tier available)',
    contextWindow: 2000000,
    inputCostPer1M: 1.25,
    outputCostPer1M: 10.00,
    supportsVision: true,
    supportsFunctionCalling: true,
  },
];

/**
 * Default AI settings for new users - All Gemini free-tier models
 */
export const DEFAULT_AI_SETTINGS: UserAISettings = {
  provider: 'gemini',
  useOwnKey: false,
  aiCredentialStatus: 'none',
  preferredModel: `google/${DEFAULT_GEMINI_FLASH_MODEL}`,
  overrides: {
    coding: `google/${DEFAULT_GEMINI_FLASH_MODEL}`,
    reasoning: `google/${DEFAULT_GEMINI_PRO_MODEL}`,
    general: `google/${DEFAULT_GEMINI_FLASH_MODEL}`,
    vision: `google/${DEFAULT_GEMINI_FLASH_MODEL}`,
    fast: `google/${DEFAULT_GEMINI_FLASH_MICRO_MODEL}`,
  },
  modelConfig: {
    temperature: 0.7,
    maxTokens: 2048,
    topP: 0.95,
    topK: 40,
  },
};
